"use client"

import { useState, useEffect, useRef } from "react"
import axios from "axios"
import Papa from 'papaparse'

const PurchaseManagement = () => {
  const [selectedItems, setSelectedItems] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [purchases, setPurchases] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 })
  const [importErrors, setImportErrors] = useState([])
  const fileInputRef = useRef(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingPurchase, setEditingPurchase] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [purchaseToDelete, setPurchaseToDelete] = useState(null)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    fetchPurchases()
  }, [])

  const fetchPurchases = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/get-purchased-products`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )
      if (response.data && response.data.products) {
        setPurchases(response.data.products)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch purchases")
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          processCSVData(results.data)
        },
        error: (error) => {
          setError("Error parsing CSV file: " + error.message)
        }
      })
    }
  }

  const processCSVData = async (data) => {
    setIsImporting(true)
    setImportProgress({ current: 0, total: data.length, success: 0, failed: 0 })
    setImportErrors([])

    for (let i = 0; i < data.length; i++) {
      const row = data[i]
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URI}/api/add-purchased-product`,
          {
            product_master_code: row.product_master_code,
            product_name: row.product_name,
            category_name: row.category_name,
            product_unit: row.product_unit,
            product_color: row.product_color,
            quantity_purchased: row.quantity_purchased,
            purchase_price_per_unit: row.purchase_price_per_unit,
            date: row.Date 
          },
          {
            headers: {
              'authorization': localStorage.getItem("accessToken"),
              'Content-Type': 'application/json'
            }
          }
        )
        setImportProgress(prev => ({
          ...prev,
          current: i + 1,
          success: prev.success + 1
        }))
      } catch (err) {
        setImportProgress(prev => ({
          ...prev,
          current: i + 1,
          failed: prev.failed + 1
        }))
        setImportErrors(prev => [...prev, {
          row: i + 1,
          error: err.response?.data?.message || "Failed to import row"
        }])
      }
    }

    // Refresh the purchases list after import
    await fetchPurchases()
    setIsImporting(false)
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(purchases.map((purchase) => purchase.id))
    } else {
      setSelectedItems([])
    }
  }

  const handleSelectItem = (id) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
  }

  const filteredPurchases = purchases.filter(
    (purchase) =>
      purchase.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.product_master_code.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const handleEditClick = (purchase) => {
    setEditingPurchase(purchase)
    setIsEditDialogOpen(true)
  }

  const handleEditPurchase = async (e) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      setError(null)
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URI}/api/edit-purchased-product/${editingPurchase.id}`,
        {
          product_master_code: editingPurchase.product_master_code,
          product_name: editingPurchase.product_name,
          category_name: editingPurchase.category_name,
          product_unit: editingPurchase.product_unit,
          product_color: editingPurchase.product_color,
          quantity_purchased: editingPurchase.quantity_purchased,
          purchase_price_per_unit: editingPurchase.purchase_price_per_unit,
          purchase_return: editingPurchase.purchase_return,
          purchase_rejected: editingPurchase.purchase_rejected,
          Date: editingPurchase.Date
        },
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the purchases list after successful update
        await fetchPurchases()
        setIsEditDialogOpen(false)
        setEditingPurchase(null)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update purchase")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteClick = (purchase) => {
    setPurchaseToDelete(purchase)
    setIsDeleteDialogOpen(true)
  }

  const handleDeletePurchase = async () => {
    try {
      setIsSubmitting(true)
      setError(null)
      
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URI}/api/delete-purchased-product/${purchaseToDelete.id}`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the purchases list after successful deletion
        await fetchPurchases()
        setIsDeleteDialogOpen(false)
        setPurchaseToDelete(null)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete purchase")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleExportClick = async () => {
    try {
      setIsExporting(true)
      setError(null)
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/export-purchase-report`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          },
          responseType: 'blob' // Important for handling file download
        }
      )

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob)
      
      // Create a temporary link element
      const link = document.createElement('a')
      link.href = url
      link.download = `purchase-report-${new Date().toISOString().split('T')[0]}.xlsx`
      
      // Append link to body, click it, and remove it
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up the URL
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.message || "Failed to export purchase report")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">Purchases</h1>
            <p className="text-gray-500">Manage your purchases</p>
          </div>
          <div className="flex gap-3">
            {/* <button 
              onClick={() => window.location.href = '/add-purchase'}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Purchase
            </button> */}
            <button 
              onClick={handleImportClick}
              disabled={isImporting}
              className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Import Purchase
            </button>
            <button 
              onClick={handleExportClick}
              disabled={isExporting}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {isExporting ? "Exporting..." : "Export Report"}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".csv"
              className="hidden"
            />
          </div>
        </div>

        {/* Import Progress */}
        {isImporting && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Importing Purchases</h3>
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progress: {importProgress.current} of {importProgress.total}</span>
                <span>Success: {importProgress.success} | Failed: {importProgress.failed}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Import Errors */}
        {importErrors.length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-red-200 p-6">
            <h3 className="text-lg font-medium text-red-900 mb-4">Import Errors</h3>
            <div className="space-y-2">
              {importErrors.map((error, index) => (
                <div key={index} className="text-sm text-red-600">
                  Row {error.row}: {error.error}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Purchase Dialog */}
        {isEditDialogOpen && editingPurchase && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative max-h-[90vh] flex flex-col">
              <button 
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 z-10"
                onClick={() => {
                  setIsEditDialogOpen(false)
                  setEditingPurchase(null)
                }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-lg font-semibold mb-4">Edit Purchase</h2>
              <div className="overflow-y-auto flex-1 pr-2">
                <form onSubmit={handleEditPurchase} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <label htmlFor="edit-code" className="block text-sm font-medium text-gray-700">Product Code</label>
                    <input
                      type="text"
                      id="edit-code"
                      value={editingPurchase.product_master_code}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, product_master_code: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter product code"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">Product Name</label>
                    <input
                      type="text"
                      id="edit-name"
                      value={editingPurchase.product_name}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, product_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter product name"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-category" className="block text-sm font-medium text-gray-700">Category</label>
                    <input
                      type="text"
                      id="edit-category"
                      value={editingPurchase.category_name}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, category_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter category name"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-unit" className="block text-sm font-medium text-gray-700">Unit</label>
                    <input
                      type="text"
                      id="edit-unit"
                      value={editingPurchase.product_unit}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, product_unit: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter product unit"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-color" className="block text-sm font-medium text-gray-700">Color</label>
                    <input
                      type="text"
                      id="edit-color"
                      value={editingPurchase.product_color}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, product_color: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter product color"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-quantity" className="block text-sm font-medium text-gray-700">Quantity</label>
                    <input
                      type="number"
                      id="edit-quantity"
                      value={editingPurchase.quantity_purchased}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, quantity_purchased: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter quantity"
                      required
                      min="0"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-price" className="block text-sm font-medium text-gray-700">Price Per Unit</label>
                    <input
                      type="number"
                      id="edit-price"
                      value={editingPurchase.purchase_price_per_unit}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, purchase_price_per_unit: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter price per unit"
                      required
                      min="0"
                      step="0.01"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-purchase-return" className="block text-sm font-medium text-gray-700">Purchase Return</label>
                    <input
                      type="number"
                      id="edit-purchase-return"
                      value={editingPurchase.purchase_return}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, purchase_return: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter purchase return"
                      required
                      min="0"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-purchase-rejected" className="block text-sm font-medium text-gray-700">Purchase Rejected</label>
                    <input
                      type="number"
                      id="edit-purchase-rejected"
                      value={editingPurchase.purchase_rejected}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, purchase_rejected: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      placeholder="Enter purchase rejected"
                      required
                      min="0"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="edit-date" className="block text-sm font-medium text-gray-700">Purchase Date</label>
                    <input
                      type="date"
                      id="edit-date"
                      value={editingPurchase.Date || new Date().toISOString().split('T')[0]}
                      onChange={(e) => setEditingPurchase(prev => ({ ...prev, Date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </form>
              </div>
              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditDialogOpen(false)
                    setEditingPurchase(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleEditPurchase}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Purchase"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Purchase Dialog */}
        {isDeleteDialogOpen && purchaseToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative max-h-[90vh] flex flex-col">
              <button 
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 z-10"
                onClick={() => {
                  setIsDeleteDialogOpen(false)
                  setPurchaseToDelete(null)
                }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-lg font-semibold mb-4">Delete Purchase</h2>
              <div className="overflow-y-auto flex-1 pr-2">
                <p className="text-sm text-gray-600">Are you sure you want to delete this purchase?</p>
              </div>
              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsDeleteDialogOpen(false)
                    setPurchaseToDelete(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleDeletePurchase}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Deleting..." : "Delete Purchase"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by product name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">{error}</div>
            ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 w-12">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === purchases.length}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Product Code</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Product Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Color</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Purchase Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Product Unit</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Price Per Unit</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Total Price</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Quantity</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Purchase Return</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Purchase Rejected</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Category</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Added By</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPurchases.map((purchase) => (
                  <tr key={purchase.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(purchase.id)}
                        onChange={() => handleSelectItem(purchase.id)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.product_master_code}</td>
                    <td className="py-4 px-4 text-sm text-gray-900">{purchase.product_name}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.product_color}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.Date ? formatDate(purchase.Date) : formatDate(purchase.Date)}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.product_unit}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">₹ {purchase.purchase_price_per_unit}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">₹ {purchase.total_price}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.quantity_purchased}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.purchase_return}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.purchase_rejected}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.category_name}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{purchase.added_by}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleEditClick(purchase)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(purchase)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Row Per Page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <svg
                  className="w-3 h-3 text-gray-400 -ml-6 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <span className="text-sm text-gray-600">Entries</span>
            </div>

            <div className="flex items-center gap-2">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button className="w-8 h-8 rounded bg-orange-500 text-white text-sm font-medium">1</button>

              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PurchaseManagement 
